/**
 * useDemoMode Hook
 *
 * Provides demo mode detection and blocking logic for the UI.
 * Prevents mutation operations in demo mode and shows upgrade modal.
 */

import { useState, useCallback } from 'react';
import { useOrganization } from '../contexts/organization-context';
import { DEMO_MODE } from '../config/constants';

export interface DemoModeHook {
  isDemo: boolean;
  blockIfDemo: () => boolean;
  showUpgradeModal: boolean;
  setShowUpgradeModal: (show: boolean) => void;
}

/**
 * Hook for managing demo mode restrictions
 *
 * Usage:
 * ```tsx
 * const { isDemo, blockIfDemo, showUpgradeModal, setShowUpgradeModal } = useDemoMode();
 *
 * const handleSave = () => {
 *   if (blockIfDemo()) return;
 *   // Proceed with save
 * };
 *
 * return (
 *   <>
 *     <button onClick={handleSave}>Save</button>
 *     <UpgradeRequiredModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
 *   </>
 * );
 * ```
 */
export function useDemoMode(): DemoModeHook {
  const { organization } = useOrganization();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Check if current organization is demo organization
  const isDemo = organization?.id === DEMO_MODE.ORGANIZATION_ID;

  /**
   * Block action if in demo mode and show upgrade modal
   * Returns true if blocked, false if allowed
   */
  const blockIfDemo = useCallback((): boolean => {
    if (isDemo) {
      setShowUpgradeModal(true);
      return true; // Blocked
    }
    return false; // Not blocked
  }, [isDemo]);

  return {
    isDemo,
    blockIfDemo,
    showUpgradeModal,
    setShowUpgradeModal,
  };
}
